import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";

const mapCountryCode = (country) => {
  if (country === "USA") return "US";
  if (country === "CAN") return "CA";
  return country || undefined;
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "13px",
      color: "#32325d",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
  hidePostalCode: true,
};

const CardElementInner = forwardRef(function CardElementInner(
  { billingDetails, onReady, onError },
  ref
) {
  const stripe = useStripe();
  const elements = useElements();
  const cardElementRef = useRef(null);
  const [error, setError] = useState("");
  const billingDetailsRef = useRef(billingDetails);
  billingDetailsRef.current = billingDetails;

  useImperativeHandle(ref, () => ({
    tokenize: async (billingDetailsOverride) => {
      const cardEl = cardElementRef.current;
      if (!stripe || !elements || !cardEl) {
        throw new Error("Stripe card entry is not ready.");
      }
      const details = billingDetailsOverride || billingDetailsRef.current || {};
      const result = await stripe.createToken(cardEl, {
        name: details.name,
        address_line1: details.streetAddress1,
        address_line2: details.streetAddress2,
        address_city: details.city,
        address_state: details.locale,
        address_zip: details.postalCode,
        address_country: mapCountryCode(details.country),
      });
      if (result.error || !result.token) {
        throw new Error(
          result.error?.message || "Unable to tokenize card with Stripe."
        );
      }
      return {
        token: result.token.id,
        last4: result.token.card?.last4,
        cardType: result.token.card?.brand,
        expMonth: result.token.card?.exp_month?.toString(),
        expYear: result.token.card?.exp_year?.toString(),
      };
    },
  }));

  return (
    <div
      style={{
        minHeight: "50px",
        marginTop: "8px",
        marginBottom: "8px",
      }}
    >
      <div
        style={{
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          padding: "12px 14px",
          backgroundColor: "#fff",
        }}
      >
        <CardElement
          options={CARD_ELEMENT_OPTIONS}
          onReady={(el) => {
            cardElementRef.current = el;
            onReady?.();
          }}
          onChange={(event) => {
            if (event.error?.message) {
              setError(event.error.message);
              onError?.(event.error.message);
            } else {
              setError("");
            }
          }}
        />
      </div>
      {error && (
        <p className="error" style={{ marginTop: "8px", marginBottom: 0 }}>
          {error}
        </p>
      )}
    </div>
  );
});

const StripeCardIframe = forwardRef(function StripeCardIframe(
  { publishableKey, billingDetails, onReady, onError },
  ref
) {
  const [stripeInstance, setStripeInstance] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!publishableKey || typeof publishableKey !== "string" || !publishableKey.trim()) {
      setStripeInstance(null);
      setLoadError(null);
      return;
    }
    setLoadError(null);
    let cancelled = false;
    loadStripe(publishableKey.trim())
      .then((stripe) => {
        if (!cancelled && stripe) {
          setStripeInstance(stripe);
          setLoadError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setStripeInstance(null);
          setLoadError(err?.message || "Failed to load Stripe. Please check your connection and try again.");
        }
      });
    return () => { cancelled = true; };
  }, [publishableKey]);

  if (!publishableKey || typeof publishableKey !== "string" || !publishableKey.trim()) {
    return (
      <div style={{ marginTop: "8px", marginBottom: "8px" }}>
        <p className="error" style={{ margin: 0 }}>
          Stripe is not configured. Missing publishable key.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ marginTop: "8px", marginBottom: "8px" }}>
        <p className="error" style={{ margin: 0 }}>
          {loadError}
        </p>
      </div>
    );
  }

  if (!stripeInstance) {
    return (
      <div style={{ marginTop: "8px", marginBottom: "8px", minHeight: "50px" }}>
        <p style={{ margin: 0, color: "#666" }}>Loading secure payment form…</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripeInstance}>
      <CardElementInner
        ref={ref}
        billingDetails={billingDetails}
        onReady={onReady}
        onError={onError}
      />
    </Elements>
  );
});

StripeCardIframe.displayName = "StripeCardIframe";

export default StripeCardIframe;
